/**
 * The pipeline, drawn as the pipeline.
 *
 * WHY THIS EXISTS. The old board showed nine panels of equal visual weight, so
 * reading "the keyword queue is saturated and it is starving the scheduler"
 * meant correlating Queues, Budget, Health and Attribution and knowing how they
 * relate. That is exactly the manual reasoning a real 19-hour stall required on
 * 2026-08-01. Here the topology IS the layout: stages left to right, rate on
 * the arrow between them, and a blocked stage lit in place. The diagnosis
 * becomes positional.
 *
 * THE GRAPH IS SERVED, NOT HARD-CODED. Nodes and edges come from
 * /metrics/topology, so a pipeline change redraws the board instead of needing
 * a matching edit in this repo. Columns are computed by longest-path depth, so
 * a new stage lands in the right place on its own.
 *
 * NO GRAPH LIBRARY. react-flow is ~50 kB for a DAG of fourteen nodes that never
 * needs dragging or zooming; the whole layout here is CSS grid plus a handful
 * of inline SVG arrows, against a current bundle of 271 kB.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { PanelState } from '../components/ui'
import { compact, n } from '../components/format'
import {
  useClassifierQueues, useClassifierTopology, useHealth, useQueues, useTopology,
} from '../api/queries'
import type { Topology, TopologyNode } from '../api/schemas'
import './FlowBoard.css'

type Status = 'flowing' | 'behind' | 'blocked' | 'idle' | 'unknown'

interface Stage {
  node: TopologyNode
  lag: number | null
  pending: number | null
  rate: number | null | undefined
  alive: number | null
  replicas: number | null
  /** sinks only — dead_letter is the one with a number worth showing */
  sinkCount: number | null
  status: Status
}

/* Geometry. Fixed so the SVG edge overlay can compute endpoints arithmetically
 * instead of measuring the DOM -- measuring means a layout pass, a resize
 * observer, and edges that lag a frame behind the boxes they connect. */
// 204, not 176. The Schematic system is monospace throughout, and mono runs
// ~15% wider than the sans it replaced -- "channel_classification" (22 chars)
// needs ~189px and was clipping to "channel_classifica…". A truncated stage
// name is the one thing on this board that must never be guessed at.
/** Preferred column width. The board SHRINKS below this to fit its container
 *  rather than overflowing -- see useColumnWidth. */
export const COL_W = 204
/** Never narrower than this: below it the stage labels ellipsis away and the
 *  board stops being readable, at which point scrolling is the lesser evil. */
export const MIN_COL_W = 132
export const COL_GAP = 46
// 112, not 96. MEASURED: at 96 the box has 2 px spare after kind + label +
// metric + one line of sub, so a sub line that needs to wrap has nowhere to go
// and gets clipped instead -- "dv360 · keywords · tier · trending" rendered as
// "tie…" and "5.99/s · 3,629 in flight" as "in fliq…". A second line costs
// ~14 px, so this is that plus margin. Every edge endpoint is derived from
// these constants, so the arrows follow on their own.
export const ROW_H = 112

/**
 * Column width that makes the graph FIT, instead of a fixed width that makes it
 * scroll.
 *
 * The collector graph is six columns; at the preferred 204 px that is ~1,450 px
 * of canvas, so on any normal window the board overflowed and the right-hand
 * stages -- dead-letter, the Postgres sink, Elasticsearch -- sat off-screen
 * behind a horizontal scrollbar. A pipeline diagram whose end you cannot see
 * has lost most of its purpose, and an edge mask only made the truncation
 * prettier.
 *
 * Measured from the container rather than a media query, because this panel
 * sits in a grid whose column count changes with the viewport -- the element's
 * own width is the only thing that actually knows how much room there is.
 */
function useColumnWidth(cols: number) {
  const ref = useRef<HTMLDivElement | null>(null)
  const [w, setW] = useState(COL_W)
  useEffect(() => {
    const el = ref.current
    if (!el || cols <= 0) return
    const fit = () => {
      const avail = el.clientWidth
      if (!avail) return
      // Total width is cols*(w+gap) - gap, so solve for w.
      const ideal = (avail - (cols - 1) * COL_GAP) / cols
      setW(Math.max(MIN_COL_W, Math.min(COL_W, Math.floor(ideal))))
    }
    fit()
    const ro = new ResizeObserver(fit)
    ro.observe(el)
    return () => ro.disconnect()
  }, [cols])
  return [ref, w] as const
}
export const ROW_GAP = 10

export interface Placed { col: number; row: number }

/** Column = longest-path depth; row = barycentre of a node's predecessors.
 *
 * COLUMN by LONGEST path, so a node always sits right of everything feeding it.
 * Shortest-path would let videos.refresh -- fed by BOTH discovery and keyword --
 * land left of one of them and draw an arrow pointing backwards.
 *
 * ROW by BARYCENTRE, the standard layered-graph heuristic: a node wants to sit
 * at the mean row of whatever feeds it, so a lane runs straight across instead
 * of zig-zagging. Columns were previously centred independently, which left a
 * two-node column floating against a three-node one and made a lane hard to
 * follow with the eye -- the whole reason for a flow layout.
 */
/**
 * Collapse every `stream -> worker` pair into ONE lane node.
 *
 * WHY, measured on the live board 2026-08-03 at 1440x900: the canvas wanted
 * 1704 px inside ~1193 px of space, and no column width fixes that — at the
 * board's own MIN_COL_W the eight columns still need 1378 px. The column
 * COUNT was the problem, not the column width.
 *
 * And half of those columns carried nothing new. A worker's queue IS its
 * stream's lag — not by coincidence in one sample but by construction, since
 * `topology()` gives every worker exactly one `reads` stream. The board was
 * drawing the same number twice, side by side, for every lane:
 *
 *     keywords.search   479.6k lag  ->  Keyword  479.6k queued
 *     channels.refresh      7k lag  ->  Channel      7k queued
 *
 * Merging halves the columns, removes the duplicate, and cuts the eye's travel
 * per lane from four columns to two. Nothing is lost: the lane keeps the
 * stream's NAME (what an operator greps for) and the worker's LIVENESS (what
 * they page on).
 *
 * DONE HERE, NOT SERVER-SIDE. `/metrics/topology` is a public endpoint and the
 * split is true of the system — it is simply not useful on screen. A
 * presentation decision belongs in the presentation layer.
 */
export function mergeLanes(topo: Topology): Topology {
  const byId = new Map(topo.nodes.map((n) => [n.id, n]))

  // worker id -> the stream(s) it reads.
  const readsOf = new Map<string, string[]>()
  topo.edges.forEach((e) => {
    if (byId.get(e.to)?.kind === 'worker' && byId.get(e.from)?.kind === 'stream') {
      const list = readsOf.get(e.to) ?? []
      list.push(e.from)
      readsOf.set(e.to, list)
    }
  })

  /** stream id -> worker id, for unambiguous 1:1 pairs only. */
  const pair = new Map<string, string>()
  readsOf.forEach((streams, worker) => {
    if (streams.length !== 1) return          // a worker on two streams stays split
    const stream = streams[0]!
    // A stream feeding two workers is a real fan-out; merging it into one of
    // them would strand the other's edge.
    const consumers = topo.edges.filter(
      (e) => e.from === stream && byId.get(e.to)?.kind === 'worker').length
    if (consumers === 1 && !pair.has(stream)) pair.set(stream, worker)
  })
  if (!pair.size) return topo

  const workerToStream = new Map<string, string>()
  pair.forEach((w, s) => workerToStream.set(w, s))

  // The lane keeps the STREAM's id, so every edge already pointing at the
  // stream resolves unchanged; only edges leaving the worker are rewired.
  const nodes = topo.nodes
    .filter((n) => !workerToStream.has(n.id))
    .map((n) => {
      const worker = pair.get(n.id)
      if (!worker) return n
      const w = byId.get(worker)!
      return {
        ...n,
        // Reports liveness and rate like a worker, keeps the stream's name.
        kind: 'worker' as const,
        // BOTH keys, because liveness is looked up by `node.role ?? node.group`
        // (see the stages memo). Carrying only `group` silently dropped the
        // lookup key for any worker the API identifies by role, so the lane
        // rendered with no replica count at all.
        group: w.group,
        role: w.role,
        laneOf: w.label,
      }
    })

  const remap = (id: string) => workerToStream.get(id) ?? id
  const seen = new Set<string>()
  const edges = topo.edges
    .map((e) => ({ ...e, from: remap(e.from), to: remap(e.to) }))
    // The merged pair's own stream->worker edge becomes a self-loop.
    .filter((e) => e.from !== e.to)
    .filter((e) => {
      const k = `${e.from} ${e.to} ${e.sweep ?? ''}`
      if (seen.has(k)) return false
      seen.add(k)
      return true
    })

  return { ...topo, nodes, edges }
}

export function layout(topo: Topology): Map<string, Placed> {
  const ids = topo.nodes.map((x) => x.id)
  const preds = new Map<string, string[]>()
  ids.forEach((id) => preds.set(id, []))
  topo.edges.forEach((e) => {
    // dead_letter is a sink every stream points at; letting it pull depth would
    // drag it far right and stretch the whole board.
    if (e.to === 'dead_letter') return
    preds.get(e.to)?.push(e.from)
  })

  const depth = new Map<string, number>()
  const visit = (id: string, seen: Set<string>): number => {
    if (depth.has(id)) return depth.get(id)!
    if (seen.has(id)) return 0                    // defensive: server asserts acyclic
    seen.add(id)
    const d = (preds.get(id) ?? []).reduce(
      (m, p) => Math.max(m, visit(p, seen) + 1), 0)
    depth.set(id, d); return d
  }
  ids.forEach((id) => visit(id, new Set()))

  // Sinks are where things END; scattering them mid-graph reads as another
  // stage, so they are pinned to the last column.
  const lastCol = Math.max(...ids.map((id) => depth.get(id) ?? 0))
  topo.nodes.forEach((x) => { if (x.kind === 'sink') depth.set(x.id, lastCol) })

  const byCol: string[][] = []
  ids.forEach((id) => { (byCol[depth.get(id) ?? 0] ??= []).push(id) })

  // Successors, for placing nodes that nothing feeds.
  const succs = new Map<string, string[]>()
  ids.forEach((id) => succs.set(id, []))
  topo.edges.forEach((e) => {
    if (e.to === 'dead_letter') return
    succs.get(e.from)?.push(e.to)
  })

  const row = new Map<string, number>()
  const deferred: string[][] = []

  // Forward pass, left to right. A node sits at the mean row of whatever feeds
  // it, so a lane runs straight across instead of zig-zagging.
  byCol.forEach((col, ci) => {
    if (!col) return
    const fed = col.filter((id) => (preds.get(id) ?? []).length > 0)
    const sources = col.filter((id) => !(preds.get(id) ?? []).length)
    deferred[ci] = sources

    if (ci === 0 && !fed.length) return    // whole column is sources

    const want = fed.map((id) => {
      const ps = (preds.get(id) ?? []).filter((p) => row.has(p))
      const m = ps.length
        ? ps.reduce((a, p) => a + row.get(p)!, 0) / ps.length : 0
      return { id, m }
    }).sort((a, b) => a.m - b.m)

    const taken = new Set<number>()
    want.forEach(({ id, m }) => {
      let r = Math.round(m)
      while (taken.has(r)) r += 1
      taken.add(r); row.set(id, r)
    })
  })

  // Backward pass for nodes NOTHING feeds.
  //
  // Barycentre only looks backwards, so a source has nothing to sit against and
  // used to fall to row 0 -- which is why the Scheduler sat top-aligned with
  // channels.discover while feeding three streams across rows 0-2, and why
  // Postgres (genuinely fed by two workers) was pushed to row 2 by sinks that
  // had no incoming edges and grabbed the earlier rows first.
  //
  // Deliberately AFTER the forward pass and second in the collision order: a
  // node with a real barycentre has a determined row and must keep it.
  for (let ci = byCol.length - 1; ci >= 0; ci--) {
    const sources = deferred[ci] ?? []
    if (!sources.length) continue
    const taken = new Set<number>(
      (byCol[ci] ?? []).filter((id) => row.has(id)).map((id) => row.get(id)!))
    sources.forEach((id) => {
      const ss = (succs.get(id) ?? []).filter((x) => row.has(x))
      // Median, not mean: a source feeding rows 0, 1 and 2 should sit on 1, and
      // the mean of an even fan lands between two rows either way.
      let r = 0
      if (ss.length) {
        const rs = ss.map((x) => row.get(x)!).sort((a, b) => a - b)
        r = Math.round(rs[Math.floor(rs.length / 2)]!)
      }
      while (taken.has(r)) r += 1
      taken.add(r); row.set(id, r)
    })
  }

  // Normalise GLOBALLY, never per column.
  //
  // This used to pull each column up to row 0 independently, which silently
  // undid the barycentre alignment it was meant to tidy: a column whose nodes
  // all landed on rows 1-2 was dragged to 0-1, so it no longer lined up with
  // the column feeding it. It is exactly why the Scheduler kept snapping back
  // to the top after being centred on the three streams it feeds.
  //
  // One offset for the whole graph keeps every relative position intact and
  // still leaves no empty band above the board.
  const min = Math.min(...ids.map((id) => row.get(id) ?? 0))
  if (min !== 0) ids.forEach((id) => row.set(id, (row.get(id) ?? 0) - min))

  const out = new Map<string, Placed>()
  ids.forEach((id) => out.set(id, { col: depth.get(id) ?? 0, row: row.get(id) ?? 0 }))
  return out
}

/** Centre-right of a node box, in the overlay's coordinate space. */
export const exitPoint = (p: Placed, colW: number = COL_W) => ({
  x: p.col * (colW + COL_GAP) + colW,
  y: p.row * (ROW_H + ROW_GAP) + ROW_H / 2,
})
/** Centre-left. */
export const entryPoint = (p: Placed, colW: number = COL_W) => ({
  x: p.col * (colW + COL_GAP),
  y: p.row * (ROW_H + ROW_GAP) + ROW_H / 2,
})

/** Derived, never hard-coded — a stage's colour has to follow its numbers or
 *  the board lies the first time the pipeline changes shape. */
function statusOf(lag: number | null, rate: number | null | undefined): Status {
  if (rate === null || rate === undefined) return 'unknown'   // not yet measurable
  if (rate === 0 && (lag ?? 0) > 1000) return 'blocked'       // work waiting, nothing moving
  if (rate === 0) return 'idle'                               // nothing waiting either
  if ((lag ?? 0) > 100_000) return 'behind'                   // moving, but losing
  return 'flowing'
}

const RATE = (r: number | null | undefined) =>
  r === null || r === undefined ? '—' : r >= 100 ? `${Math.round(r)}/s` : `${r}/s`

export function FlowBoard({ onSelect }: { onSelect?: (id: string) => void }) {
  return <Board topo={useTopology()} rows={useQueues().data?.streams}
                health={useHealth().data?.roles} onSelect={onSelect} />
}

/** The classifier's flow. Same component, different source: forking it would
 *  mean every future fix to layout or status derivation had to be made twice. */
export function ClassifierFlowBoard(
  { onSelect }: { onSelect?: (id: string) => void },
) {
  const q = useClassifierQueues()
  // The classifier's workers report liveness on the SAME heartbeat keys, but
  // through the collector's /metrics/health, which only knows collector roles.
  // So a classifier stage shows its queue state and not an alive count -- an
  // invented "1/1 live" would be worse than an honest blank.
  // The classifier's workers DO heartbeat -- into their own Redis database,
  // which /metrics/classifier/queues now reads. Mapped onto the same shape the
  // collector's health endpoint returns so Board needs no second code path.
  const roles = q.data?.workers
    ? Object.entries(q.data.workers).map(([role, alive]) => ({
        group: role, consumers: alive, alive,
      }))
    : undefined
  return (
    <>
      <Board topo={useClassifierTopology()} rows={q.data?.streams}
             health={roles} onSelect={onSelect}
             llm={q.data?.llm ?? null} slots={q.data?.fleet_slots} />
      {q.data && q.data.configured === false ? (
        <p className="fb-note">
          <b>Queue figures unavailable.</b> CLASSIFIER_REDIS_URL is not set, so
          the stages show their shape but no numbers. The graph is still true.
        </p>
      ) : null}
    </>
  )
}

interface BoardProps {
  topo: { data?: Topology | undefined; isLoading: boolean; error: unknown }
  rows: { stream: string; lag: number | null; pending: number | null
          depth?: number | undefined
          rate_per_s?: number | null | undefined }[] | undefined
  health: { group: string; consumers: number; alive: number }[] | undefined
  onSelect?: ((id: string) => void) | undefined
  llm?: { used: number; cap: number } | null
  slots?: number | undefined
}

function Board({ topo, rows, health, onSelect, llm, slots }: BoardProps) {
  // THE GRAPH THE BOARD DRAWS, which is not the graph the API sends. Every
  // stream/worker pair is collapsed into one lane -- see mergeLanes for the
  // measurement that forced it. Derived once here so layout, edges and nodes
  // all draw the same graph; deriving it twice is how a board ends up with
  // edges pointing at nodes it no longer renders.
  const graph = useMemo(
    () => (topo.data ? mergeLanes(topo.data) : undefined), [topo.data])

  const stages = useMemo(() => {
    if (!graph) return null
    const llmRef = llm ?? null
    const byStream = new Map((rows ?? []).map((s) => [s.stream, s]))
    const byGroup = new Map((health ?? []).map((r) => [r.group, r]))

    const m = new Map<string, Stage>()
    graph.nodes.forEach((node) => {
      const q = byStream.get(node.id)
      const key = node.role ?? node.group
      const h = key ? byGroup.get(key) : undefined
      // A worker's throughput is the rate of the stream it reads.
      //
      // A MERGED LANE IS EXEMPT, and this is load-bearing. A lane's id IS its
      // stream's id, so `q` above is already its own row -- but its `kind` is
      // `worker`, and after the merge the edge feeding it comes from the
      // UPSTREAM LANE, whose id is also a stream id. So the walk-back still
      // found a row and still looked right; it was just the wrong lane's row.
      // Measured: videos.refresh at 481,688 lag and 0/s rendered "0 backlog"
      // and coloured IDLE, because it read channels.discover instead -- the
      // one genuinely blocked lane on the board, painted as the quiet one.
      const readsFrom = node.laneOf
        ? undefined
        : graph.edges.find(
            (e) => e.to === node.id && byStream.has(e.from))?.from
      const src = node.kind === 'worker' && readsFrom
        ? byStream.get(readsFrom) : q
      const lag = src?.lag ?? null
      const rate = src?.rate_per_s
      m.set(node.id, {
        node,
        lag,
        pending: src?.pending ?? null,
        rate,
        alive: h?.alive ?? null,
        replicas: h?.consumers ?? null,
        sinkCount: node.id === 'dead_letter'
          ? (byStream.get('dead_letter')?.depth ?? null) : null,
        status: node.kind === 'sink'
          ? (node.id === 'dead_letter'
             && (byStream.get('dead_letter')?.depth ?? 0) > 0 ? 'behind' : 'flowing')
          : node.kind === 'producer' ? 'flowing'
            : node.kind === 'upstream'
              ? (node.id === 'deepseek' && llmRef && llmRef.used >= llmRef.cap
                 ? 'blocked' : 'flowing')
              : statusOf(lag, rate),
      })
    })
    return m
  }, [graph, rows, health, llm])

  const place = useMemo(() => (graph ? layout(graph) : new Map()), [graph])

  // Fit the graph to the panel instead of letting it overflow. Six columns at
  // the preferred width is ~1,450 px, which pushed dead-letter, the Postgres
  // sink and Elasticsearch off-screen behind a scrollbar.
  const cols = useMemo(
    () => (place.size ? Math.max(...[...place.values()].map((p) => p.col)) + 1 : 0),
    [place])
  const [fitRef, colW] = useColumnWidth(cols)

  const canvas = useMemo(() => {
    if (!place.size) return { w: 0, h: 0 }
    const cs = [...place.values()]
    return {
      w: (Math.max(...cs.map((p) => p.col)) + 1) * (colW + COL_GAP) - COL_GAP,
      h: (Math.max(...cs.map((p) => p.row)) + 1) * (ROW_H + ROW_GAP) - ROW_GAP,
    }
  }, [place, colW])

  return (
    <PanelState isLoading={topo.isLoading} error={topo.error}>
      {graph && stages ? (
        <div className="fb">
          <div className="fb-legend">
            <span className="fb-lg"><i data-s="flowing" />flowing</span>
            <span className="fb-lg"><i data-s="behind" />behind</span>
            <span className="fb-lg"><i data-s="blocked" />blocked</span>
            <span className="fb-lg"><i data-s="idle" />idle</span>
            <span className="fb-lg"><i data-s="unknown" />measuring</span>
          </div>

          <div className="fb-scroll" ref={fitRef}>
            <div className="fb-canvas" style={{ width: canvas.w, height: canvas.h }}>
              {/* Edges UNDER the boxes, one per edge rather than one per column
                  gap, and each takes the colour of the stage it leaves. A lane
                  that is behind is now a continuous amber path across the board
                  instead of amber boxes joined by grey connectors. */}
              <svg className="fb-edges" width={canvas.w} height={canvas.h}
                   viewBox={`0 0 ${canvas.w} ${canvas.h}`} aria-hidden="true">
                {graph.edges.map((e, i) => {
                  const a = place.get(e.from); const b = place.get(e.to)
                  if (!a || !b || b.col <= a.col) return null
                  const p1 = exitPoint(a, colW); const p2 = entryPoint(b, colW)
                  const mx = (p1.x + p2.x) / 2
                  const st = stages.get(e.from)?.status ?? 'unknown'
                  // Every stream also points at dead_letter. Drawn faintly and
                  // dashed: five converging solid lines would be noise, but
                  // omitting them entirely would leave the sink unconnected.
                  const dl = e.to === 'dead_letter'
                  return (
                    <path
                      key={i}
                      className="fb-edge"
                      data-status={dl ? 'dead' : st}
                      d={`M${p1.x},${p1.y} C${mx},${p1.y} ${mx},${p2.y} ${p2.x},${p2.y}`}
                      markerEnd={dl ? undefined : `url(#fb-a-${st})`}
                    />
                  )
                })}
                <defs>
                  {['flowing', 'behind', 'blocked', 'idle', 'unknown'].map((k) => (
                    <marker key={k} id={`fb-a-${k}`} viewBox="0 0 8 8"
                            refX="7" refY="4" markerWidth="7" markerHeight="7"
                            orient="auto-start-reverse">
                      <path d="M0,1 L7,4 L0,7 Z" className="fb-head" data-status={k} />
                    </marker>
                  ))}
                </defs>
              </svg>

              {graph.nodes.map((node) => {
                const st = stages.get(node.id)!
                const p = place.get(node.id)!
                return (
                  <button
                    key={node.id}
                    className="fb-node"
                    data-kind={node.kind}
                    data-status={st.status}
                    style={{
                      left: p.col * (colW + COL_GAP),
                      top: p.row * (ROW_H + ROW_GAP),
                      width: colW, height: ROW_H,
                    }}
                    onClick={() => onSelect?.(node.id)}
                    title={`${node.label} — ${st.status}`}
                  >
                    {/* The eyebrow carries the worker's name on a merged
                        lane, so the role is still legible even though the box
                        is titled with the stream it drains. */}
                    <span className="fb-kind">{node.laneOf ?? node.kind}</span>
                    <span className="fb-label">{node.label}</span>
                    {node.kind === 'producer' ? (
                      /* A producer has no queue, so lag and rate read as "—".
                         What it HAS is sweeps. */
                      <span className="fb-sub">
                        {(node.sweeps ?? []).join(' · ') || 'scheduled'}
                      </span>
                    ) : node.kind === 'upstream' ? (
                      /* THE STAGE THAT WAS MISSING. The eleven-hour stall was a
                         DeepSeek daily cap; with the upstream off the board
                         there was nowhere for it to show. vLLM has no live rate
                         because this API cannot reach thor -- capacity only,
                         rather than a number we would have to invent. */
                      llm && node.id === 'deepseek' ? (
                        <>
                          <span className="fb-metric">
                            {compact(llm.used)}<em> today</em>
                          </span>
                          <span className="fb-sub">
                            cap {compact(llm.cap)}
                            {llm.used >= llm.cap ? ' · rate-gated' : ''}
                          </span>
                          <span className="fb-track"><i style={{
                            width: `${Math.min(100, (llm.used / llm.cap) * 100)}%`,
                          }} /></span>
                        </>
                      ) : node.id === 'vllm' && (node.slots ?? slots) ? (
                        /* Two lines, not one. The single nowrap line truncated
                           to "292 slots · no live ra…" at 176 px; the metric
                           slot is empty for this node anyway. */
                        <>
                          <span className="fb-metric">
                            {node.slots ?? slots}<em> slots</em>
                          </span>
                          <span className="fb-sub">no live rate</span>
                        </>
                      ) : (
                        <span className="fb-sub">upstream</span>
                      )
                    ) : node.kind === 'sink' ? (
                      <span className="fb-sub fb-sink">
                        {st.sinkCount !== null
                          ? `${compact(st.sinkCount)} parked` : 'terminal'}
                      </span>
                    ) : (
                      <>
                        <span className="fb-metric">
                          {st.lag === null ? '—' : compact(st.lag)}
                          {/* A worker is not itself behind by N -- the queue it
                              reads is. "queued" says whose number it is. */}
                          {/* A merged lane owns both halves, so "backlog" is
                              the honest word -- neither "lag" (the stream's
                              word) nor "queued" (the worker's) is wrong, and
                              picking one implied the other number existed
                              elsewhere. Unmerged nodes keep their own noun. */}
                          <em>{node.laneOf ? ' backlog'
                            : node.kind === 'stream' ? ' lag' : ' queued'}</em>
                        </span>
                        <span className="fb-sub">
                          {RATE(st.rate)}
                          {st.replicas !== null
                            ? ` · ${st.alive}/${st.replicas} live`
                            : st.pending !== null
                              ? ` · ${n(st.pending)} in flight` : ''}
                        </span>
                        <span className="fb-track">
                          <i style={{ width: `${Math.min(100,
                            ((st.lag ?? 0) / 1_000_000) * 100)}%` }} />
                        </span>
                      </>
                    )}
                  </button>
                )
              })}
            </div>
          </div>

          <p className="fb-note">
            Rates are measured between your last two loads, not live — the board
            is pulled on demand. <b>“measuring”</b> means only one sample exists
            so far; it is not zero.
          </p>
        </div>
      ) : null}
    </PanelState>
  )
}
